import { yupResolver } from '@hookform/resolvers/yup';
import { pageIntroContent } from '@insify/css/classes.module.css';
import { Button, Input, Modal } from '@insify/design-system/components';
import { headingColor, headingSm } from '@insify/design-system/typography';
import { emailRegex } from '@insify/global-validation';
import { ContentContextWrapper } from '@objectiv/tracker-react';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import * as styles from './RequestSaveQuoteEmail.module.css';

import type { SubmitHandler } from 'react-hook-form';

export type SaveQuoteEmailFormType = {
  email: string;
};

export type RequestSaveQuoteEmailProps = {
  translations: {
    close: string;
    required: string;
    emailInvalid: string;
    saveQuoteEmailButton: string;
    saveQuoteEmailModalSubmitTitle: string;
    saveQuoteEmailModalSubmitLabel: string;
    saveQuoteEmailModalSubmitButton: string;
  };
  email?: string;

  onSubmitForm: (data: SaveQuoteEmailFormType) => Promise<void>;
};

export const RequestSaveQuoteEmail = ({ translations, onSubmitForm, email }: RequestSaveQuoteEmailProps) => {
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState } = useForm<SaveQuoteEmailFormType>({
    resolver: yupResolver(
      yup.object().shape({
        email: yup.string().required(translations.required).matches(emailRegex, translations.emailInvalid),
      }),
    ),
    shouldUnregister: true,
    defaultValues: {
      email: email || '',
    },
    mode: 'onSubmit',
  });

  const onSubmit: SubmitHandler<SaveQuoteEmailFormType> = async (formData) => {
    setIsSubmitting(true);
    try {
      await onSubmitForm(formData);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
    } finally {
      setIsSubmitting(false);
      setShowModal(false);
    }
  };

  return (
    <ContentContextWrapper id='coverage-request-save-quote-email'>
      <Button
        type='button'
        trackingId='open'
        kind='secondary'
        className={styles.requestSaveQuoteEmailButton}
        onClick={() => {
          setShowModal(true);
        }}
        data-testid='receipt-quote'
      >
        {translations.saveQuoteEmailButton}
      </Button>
      <Modal
        trackingId='modal'
        open={showModal}
        onClose={() => {
          setShowModal(false);
        }}
      >
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            return handleSubmit(onSubmit)(e);
          }}
        >
          <h3 className={clsx(headingSm, headingColor, pageIntroContent)}>
            {translations.saveQuoteEmailModalSubmitTitle}
          </h3>
          <Input
            {...register('email')}
            id='email'
            trackingId='email'
            error={formState.errors.email?.message}
            disabled={isSubmitting}
            className={styles.modalEmailInput}
            label={translations.saveQuoteEmailModalSubmitLabel}
          />
          <Button
            trackingId='save'
            type='submit'
            kind='primary'
            loading={isSubmitting}
            className={styles.modalSubmitButton}
            data-testid='submit-request-save-quote-email'
          >
            {translations.saveQuoteEmailModalSubmitButton}
          </Button>
        </form>
      </Modal>
    </ContentContextWrapper>
  );
};
